import React from "react";
import { useTenant } from "../context/TenantContext";
import "../styles/terms.css";

const Terms = () => {
  const { tenant } = useTenant();

  // Helper function to format phone number for tel: links (same as Contact page)
  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  // Get terms content from tenant (loaded from backend via TermsContent field)
  const termsContent = tenant?.TermsContent;

  // Check if we have custom content from the admin
  const hasCustomContent = termsContent && termsContent.trim() !== "";

  // If custom content exists, render it directly
  if (hasCustomContent) {
    return (
      <div className="terms-page">
        <div className="container">
          <section className="terms-hero">
            <h1>Terms & Conditions</h1>
            <p>Please read these terms carefully before using our services</p>
          </section>
          <div
            className="terms-content"
            dangerouslySetInnerHTML={{ __html: termsContent }}
          />
        </div>
      </div>
    );
  }

  // Default content using tenant data from backend (same approach as Contact page)
  return (
    <div className="terms-page">
      <div className="container">
        <section className="terms-hero">
          <h1>Terms & Conditions</h1>
          <p>Please read these terms carefully before using our services</p>
        </section>

        <div className="terms-content">
          <div className="terms-section">
            <h2>Terms & Conditions</h2>
            <p>
              Please read these terms carefully before using the services
              provided by {tenant?.BusinessName || "our company"}.
            </p>
          </div>

          <div className="terms-section">
            <h3>1. Acceptance of Terms</h3>
            <p>
              By accessing and using our website, you agree to be bound by these
              Terms & Conditions. If you do not agree with any part of these
              terms, please do not use our services.
            </p>
          </div>

          <div className="terms-section">
            <h3>2. Products and Pricing</h3>
            <p>
              We strive to display accurate product information and pricing.
              However, we reserve the right to correct any errors, inaccuracies,
              or omissions. Prices are subject to change without notice.
            </p>
          </div>

          <div className="terms-section">
            <h3>3. Orders and Payments</h3>
            <p>
              All orders are subject to acceptance and availability. Payment
              must be received in full before orders are processed. We reserve
              the right to refuse or cancel any order at our discretion.
            </p>
          </div>

          <div className="terms-section">
            <h3>4. Shipping and Delivery</h3>
            <p>
              Delivery times are estimates and not guaranteed. We are not
              responsible for delays caused by carriers, weather conditions, or
              other factors beyond our control.
            </p>
          </div>

          <div className="terms-section">
            <h3>5. Returns and Refunds</h3>
            <p>
              Please review our return policy before making a purchase. Returns
              are accepted within 30 days of delivery, subject to our return
              policy conditions.
            </p>
          </div>

          <div className="terms-section">
            <h3>6. Privacy and Data Protection</h3>
            <p>
              Your privacy is important to us. Please review our Privacy Policy
              to understand how we collect, use, and protect your personal
              information.
            </p>
          </div>

          <div className="terms-section">
            <h3>7. Intellectual Property</h3>
            <p>
              All content on this website, including text, images, logos, and
              designs, is the property of{" "}
              {tenant?.BusinessName || "our company"}
              and is protected by copyright and intellectual property laws.
            </p>
          </div>

          <div className="terms-section">
            <h3>8. Limitation of Liability</h3>
            <p>
              {tenant?.BusinessName || "We"} shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages
              arising from your use of our services.
            </p>
          </div>

          <div className="terms-section">
            <h3>9. Governing Law</h3>
            <p>
              These terms shall be governed by and construed in accordance with
              the laws of South Africa. Any disputes shall be subject to the
              exclusive jurisdiction of the courts of South Africa.
            </p>
          </div>

          <div className="terms-section">
            <h3>10. Contact Information</h3>
            <p>
              If you have any questions about these Terms & Conditions, please
              contact us:
            </p>

            {/* Email - from backend */}
            {tenant?.BusinessEmail && (
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${tenant.BusinessEmail}`}>
                  {tenant.BusinessEmail}
                </a>
              </p>
            )}

            {/* Phone - from backend with proper formatting */}
            {tenant?.BusinessPhone && (
              <p>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                  {tenant.BusinessPhone}
                </a>
              </p>
            )}

            {/* Address - from backend */}
            {(tenant?.AddressLine1 || tenant?.City) && (
              <p>
                <strong>Address:</strong>{" "}
                {[
                  tenant?.AddressLine1,
                  tenant?.AddressLine2,
                  tenant?.City,
                  tenant?.State,
                  tenant?.PostalCode,
                  tenant?.Country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}

            {/* Fallback contact info if no tenant data exists */}
            {!tenant?.BusinessEmail &&
              !tenant?.BusinessPhone &&
              !tenant?.AddressLine1 &&
              !tenant?.City && (
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:terms@example.com">terms@example.com</a>
                </p>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
